import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Output,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BaseComponent } from '@core/base.component';
import { base64, createId } from '@core/util/file';
import { ICON } from '@core/util/icon';
import { base64ToBlob } from '@core/util/string';
import { BusinessUser } from '@models/businesses';
import { SYNC_FLOW_TYPES } from '@models/mortgage';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { CloserSyncService } from '@services/closer-sync/closer-sync.service';
import { SnackbarService } from '@services/ui/snackbar.service';
import { AuthState } from '@state/auth';
import { BusinessState } from '@state/business';
import { FileDownloadDispatch } from '@state/file-download/fileDownload.index';
import {
	FileDownload,
	FileDownloadType,
} from '@state/file-download/fileDownload.model';
import {
	FileUpload,
	FileUploadState,
} from '@state/file-upload/fileUpload.state';
import { SelectedMatter } from '@state/matter/matter.selectors';
import LogRocket from 'logrocket';
import { lastValueFrom, Observable } from 'rxjs';
import {
	CloserSyncInstructionsAPIService,
	Package,
	PackageType,
	PreparePackageRequest,
} from 'src/app/shared/api-repos/mortgages/closer-sync-instructions.api.service';

// states
export enum State {
	LOADING = 'loading',
	REVIEW = 'review',
	SENT = 'sent',
	SENDING = 'sending',
	ERROR_LOADING = 'errorLoading',
}

export interface LenderDocument {
	id?: string;
	name: string;
	path: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fields?: any;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	labels?: any;
}

export enum MortgageStepType {
	REQUEST_FOR_FUNDS = 'requestForFunds',
	FINAL_REPORT = 'finalReport',
}

export interface SendPackageToLenderComponentDialogData {
	mortgageId: string;
	instructionId: string;
	lawyerId: string;
	mortgageStep: MortgageStepType;
	requiresLawyerSignature: boolean;
	documents: LenderDocument[];
	lenderName: string;
	syncFlowType: SYNC_FLOW_TYPES;
}

interface VM {
	fileName: string;
	fileBase64: base64;
	verifyContent: boolean;
	pin: string;
	state: State;
	formSubmitted: boolean;
	errorSending: boolean;
}

const DefaultVM: VM = {
	fileName: '',
	fileBase64: null,
	verifyContent: false,
	pin: null,
	state: State.LOADING,
	formSubmitted: false,
	errorSending: false,
};

@UntilDestroy()
@Component({
	selector: 'app-send-package-to-lender',
	templateUrl: './send-package-to-lender.component.html',
	styleUrls: ['./send-package-to-lender.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SendPackageToLenderComponent
	extends BaseComponent<VM>
	implements OnInit
{
	_data: SendPackageToLenderComponentDialogData;
	fileBase64: string;
	packageId = createId();
	sendingToLender = false;
	SYNC_FLOW_TYPES = SYNC_FLOW_TYPES;
	STATES = State;
	assignedLawyerId: string;
	assignedLawyerPin: string;
	ICON = ICON;

	@Select(SelectedMatter.assignedLawyerUser)
	assignedLawyer$: Observable<BusinessUser>;
	@Select(AuthState.getUserId) userId$: Observable<string>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(FileUploadState.getFiles) uploadingFiles$: Observable<FileUpload[]>;
	@SelectSnapshot(BusinessState.businessId) businessId: string;
	@SelectSnapshot(BusinessState.businessName) businessName: string;
	@SelectSnapshot(SelectedMatter.matterNumber) matterNumber: string;
	@SelectSnapshot(SelectedMatter.matterName) matterName: string;

	@Output() sendToLenderStateChanged = new EventEmitter<string>();

	constructor(
		private dialogRef: MatDialogRef<SendPackageToLenderComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: SendPackageToLenderComponentDialogData,
		private closerSyncInstructionsAPIService: CloserSyncInstructionsAPIService,
		private snackbarService: SnackbarService,
		private closerSyncService: CloserSyncService,
		private analytics: AnalyticsService,
		private fileDownloadDispatch: FileDownloadDispatch,
	) {
		super(DefaultVM);
		this.dialogRef.updateSize('80vw', '90vh');
		this._data = data;
	}

	onStateChanged() {
		this.sendToLenderStateChanged.emit(this.VM.state);
	}

	async ngOnInit(): Promise<void> {
		this.assignedLawyer$.pipe(untilDestroyed(this)).subscribe((lawyer) => {
			this.assignedLawyerId = lawyer?.id;
			this.assignedLawyerPin = lawyer?.accessPin;
		});

		await this.load();
	}

	async load() {
		this.VM = { state: State.LOADING };
		this.onStateChanged();
		try {
			const documents: PreparePackageRequest[] = this._data.documents.map(
				(document) => ({
					formId: document.id,
					path: document.path,
					fieldOverrides: document.fields,
				}),
			);

			// either sent to RFF or FinalReport Endpoint
			const fileBase64 = await lastValueFrom(
				this.closerSyncInstructionsAPIService.preparePackage(
					this.assignedLawyerId,
					documents,
				),
			);

			this.VM = {
				fileBase64: { data: fileBase64, encoding: FileDownloadType.BASE_64 },
				state: State.REVIEW,
			};
			this.onStateChanged();
		} catch {
			this.dialogRef.close();
		}
	}

	async downloadFiles(form: NgForm) {
		this.VM = { formSubmitted: true, errorSending: false };

		if (form.invalid) {
			console.log('Invalid form');
			return;
		}

		const isPinValid = await this.closerSyncService.checkPin(
			this.VM.pin,
			this.assignedLawyerPin,
		);

		if (!isPinValid) {
			this.snackbarService.openSnackBar({
				text: 'Invalid Access PIN',
				type: 'error',
			});
			return;
		}

		try {
			this.analytics.logEvent(AnalyticsEvent.SYNC_MORTGAGE_PAGE_DOWNLOAD);

			const blob = base64ToBlob(this.VM.fileBase64.data, 'application/pdf');

			const fileDownload: FileDownload = {
				name:
					this._data.documents?.length > 1
						? 'Mortgage Documents'
						: this._data.documents[0]?.name,
				type: 'pdf',
				data: blob,
				dataType: FileDownloadType.BLOB,
			};

			this.fileDownloadDispatch.DownloadFile(fileDownload);
		} catch (err) {
			this.snackbarService.openSnackBar({
				text: 'Unable to download file',
				type: 'error',
			});
			LogRocket.captureException(err);
		} finally {
			this.VM = { formSubmitted: false };
		}
	}

	async uploadDocument(form: NgForm) {
		this.VM = { formSubmitted: true, errorSending: false };

		if (form.invalid) {
			console.log('Invalid form');
			return;
		}

		const isPinValid = await this.closerSyncService.checkPin(
			this.VM.pin,
			this.assignedLawyerPin,
		);

		if (!isPinValid) {
			this.snackbarService.openSnackBar({
				text: 'Invalid Access PIN',
				type: 'error',
			});
			return;
		}

		try {
			this.analytics.logEvent(AnalyticsEvent.SYNC_MORTGAGE_PAGE_SEND);
			this.VM = { state: State.SENDING };
			this.onStateChanged();
			const type =
				this._data.mortgageStep === 'requestForFunds'
					? PackageType.RequestForFunds
					: PackageType.FinalReport;
			const _package: Package = {
				packageId: this.packageId,
				mortgageId: this._data.mortgageId,
				matterName: this.matterName,
				matterNumber: this.matterNumber,
				businessId: this.businessId,
				businessName: this.businessName,
				supportingDocument: !this._data.requiresLawyerSignature,
				matterId: this.matterId,
				packageName: this.VM.fileName,
				packageDescription: type,
				type,
				file: {
					name: this.VM.fileName,
					fileBase64: this.VM.fileBase64.data,
					mergedDocuments: this._data.documents.map((document) => {
						return {
							id: document.id,
							name: document.name,
							type: document?.labels?.formType,
						};
					}),
				},
			};

			// call the api from here
			await lastValueFrom(
				this.closerSyncInstructionsAPIService.sendLenderDocumentPackage(
					this.assignedLawyerId,
					this._data.instructionId,
					_package,
				),
			);
			this.VM = { state: State.SENT };
			this.onStateChanged();
		} catch (err) {
			LogRocket.captureException(err);
			this.snackbarService.openSnackBar({
				text: 'Error: Unable to send Package to Lender',
				type: 'error',
			});
			this.VM = { state: State.REVIEW };
			this.onStateChanged();
		}
	}
	back() {
		this.dialogRef.close();
	}
}
